import React, { useState, useCallback, useMemo } from 'react';
import api from "../services/api";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";
import useToast from "../hooks/useToast";
import { PlusIcon, TrashIcon, XMarkIcon,DevicePhoneMobileIcon, UserPlusIcon } from '@heroicons/react/24/outline';

const AssignScanners = () => {
  const { showSuccess, showError } = useToast();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningScannerId, setAssigningScannerId] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [newScanner, setNewScanner] = useState({ name: "", email: "", password: "" });

  // ✅ React Query: Fetch devices
  const {
    data: devices = [],
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useApiQuery(
    ['devices'],
    () => api.get("/devices").then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError("Failed to load devices"),
    }
  );

  // ✅ React Query: Fetch users (scanners)
  const {
    data: allUsers = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery(
    ['users'],
    () => api.get("/auth/users").then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError("Failed to load users"),
    }
  );

  // ✅ Filter scanners from users
  const scanners = useMemo(() => {
    return allUsers.filter((u) => u.role === "device_scanner");
  }, [allUsers]);

  const isLoading = devicesLoading || usersLoading;

  // ✅ Get unique events from devices
  const uniqueEvents = useMemo(() => {
    const eventMap = new Map();
    devices.forEach(device => {
      if (device.eventId?._id) {
        eventMap.set(device.eventId._id, device.eventId);
      }
    });
    return Array.from(eventMap.values());
  }, [devices]);

  // ✅ Get occupied device IDs
  const getOccupiedDeviceIds = useCallback((targetScannerId) => {
    const occupied = new Set();
    scanners.forEach(scanner => {
      if (scanner._id === targetScannerId) return;
      scanner.assignedDevices?.forEach(devId => occupied.add(devId));
    });
    return occupied;
  }, [scanners]);

  // ✅ Filter devices by selected event
  const filteredDevices = useMemo(() => {
    return selectedEventId
      ? devices.filter(d => d.eventId?._id === selectedEventId)
      : [];
  }, [devices, selectedEventId]);

  // ✅ Occupied IDs for modal
  const occupiedIds = useMemo(() => {
    return showAssignModal && assigningScannerId
      ? getOccupiedDeviceIds(assigningScannerId)
      : new Set();
  }, [showAssignModal, assigningScannerId, getOccupiedDeviceIds]);

  // ✅ React Query: Create scanner mutation
  const createScannerMutation = useApiMutation(
    (data) => api.post("/auth/register", data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Device scanner created");
        setShowCreateModal(false);
        setNewScanner({ name: "", email: "", password: "" });
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Creation failed");
      },
    }
  );

  // ✅ React Query: Delete scanner mutation
  const deleteScannerMutation = useApiMutation(
    (id) => api.delete(`/auth/users/${id}`).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Scanner deleted");
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Delete failed");
      },
    }
  );

  // ✅ React Query: Unassign device mutation
  const unassignDeviceMutation = useApiMutation(
    ({ scannerId, deviceId }) =>
      api.delete(`/devices/${deviceId}/scanner/${scannerId}`).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Device unassigned");
        refetchDevices();
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Failed to unassign");
      },
    }
  );

  // ✅ React Query: Assign devices mutation
  const assignDevicesMutation = useApiMutation(
    ({ scannerUserId, deviceIds }) =>
      api.post("/devices/assign-to-scanner", { scannerUserId, deviceIds }).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Devices assigned");
        setShowAssignModal(false);
        setAssigningScannerId(null);
        setSelectedDeviceIds([]);
        refetchDevices();
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Assignment failed");
      },
    }
  );

  // ✅ Handlers
  const handleCreateScanner = useCallback((e) => {
    e.preventDefault();
    if (!newScanner.name || !newScanner.email || !newScanner.password) {
      showError("All fields required");
      return;
    }
    createScannerMutation.mutate({ ...newScanner, role: "device_scanner" });
  }, [newScanner, createScannerMutation, showError]);

  const handleDeleteScanner = useCallback((id, name) => {
    if (!window.confirm(`Delete scanner "${name}"? This will also remove all device assignments.`)) return;
    deleteScannerMutation.mutate(id);
  }, [deleteScannerMutation]);

  const handleUnassignDevice = useCallback((scannerId, deviceId, deviceName) => {
    if (!window.confirm(`Remove device "${deviceName}" from this scanner?`)) return;
    unassignDeviceMutation.mutate({ scannerId, deviceId });
  }, [unassignDeviceMutation]);

  const openAssignModal = useCallback((scannerId) => {
    setAssigningScannerId(scannerId);
    setSelectedDeviceIds([]);
    setSelectedEventId("");
    setShowAssignModal(true);
  }, []);

  const handleAssignDevices = useCallback(() => {
    if (!assigningScannerId || selectedDeviceIds.length === 0) {
      showError("Select at least one device");
      return;
    }
    assignDevicesMutation.mutate({
      scannerUserId: assigningScannerId,
      deviceIds: selectedDeviceIds,
    });
  }, [assigningScannerId, selectedDeviceIds, assignDevicesMutation, showError]);

  const toggleDeviceSelection = useCallback((deviceId) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  }, []);

  const getDeviceName = useCallback((deviceId) => {
    return devices.find((d) => d._id === deviceId)?.name || deviceId;
  }, [devices]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-[#708090] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createScannerMutation.isPending ||
                       deleteScannerMutation.isPending ||
                       unassignDeviceMutation.isPending ||
                       assignDevicesMutation.isPending;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#2b3445]">
            Device Scanner Management
          </h1>
          <p className="text-sm text-[#708090] mt-1">
            Create scanners and assign devices
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-[8px] text-sm font-bold shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          New Scanner
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#dae1e7] bg-[#f6f9fc]">
          <h2 className="text-lg font-semibold text-[#2b3445]">
            Device Scanners
            <span className="ml-2 text-sm font-normal text-[#708090]">
              ({scanners.length})
            </span>
          </h2>
        </div>

        {scanners.length === 0 ? (
          <div className="p-8 text-center text-[#708090]">
            No scanners created yet. Click "New Scanner" to add one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#dae1e7]">
              <thead className="bg-[#f6f9fc]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#708090] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#708090] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#708090] uppercase tracking-wider">
                    Assigned Devices
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#708090] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#dae1e7]">
                {scanners.map((scanner) => (
                  <tr key={scanner._id} className="hover:bg-[#f6f9fc] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#2b3445]">
                      {scanner.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#708090]">
                      {scanner.email}
                    </td>
                   <td className="px-6 py-4">
  {scanner.assignedDevices && scanner.assignedDevices.length > 0 ? (
    <div className="flex flex-wrap gap-2">
      {scanner.assignedDevices.map((deviceId) => {
        const name = getDeviceName(deviceId);
        return (
          <span
            key={deviceId}
            className="group inline-flex items-center gap-1.5 bg-white border border-[#1672b9]/30 text-[#1672b9] rounded-full pl-2.5 pr-1 py-1 text-xs font-medium shadow-sm hover:border-[#1672b9] transition-colors"
          >
            <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-[#1672b9]" />
            <span>{name}</span>
            <button
              onClick={() => handleUnassignDevice(scanner._id, deviceId, name)}
              disabled={isSubmitting}
              className="w-4 h-4 flex items-center justify-center rounded-full text-[#1672b9]/60 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-colors"
              title={`Remove ${name}`}
              aria-label={`Remove ${name}`}
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        );
      })}
    </div>
  ) : (
    <span className="text-[#708090] text-sm italic">No devices assigned</span>
  )}
</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openAssignModal(scanner._id)}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border border-[#1672b9] text-[#1672b9] rounded-[6px] hover:bg-[#1672b9]/5 transition disabled:opacity-50 mr-2"
                      >
                        <PlusIcon className="w-3 h-3" />
                        Add Devices
                      </button>
                      <button
                        onClick={() => handleDeleteScanner(scanner._id, scanner.name)}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border border-[#dae1e7] text-[#708090] rounded-[6px] hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition disabled:opacity-50"
                      >
                        {deleteScannerMutation.isPending && deleteScannerMutation.variables === scanner._id ? (
                          <>
                            <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <TrashIcon className="w-3 h-3" />
                            Delete
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Scanner Modal */}
      {showCreateModal && (
        <Modal title="Create Device Scanner" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreateScanner} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                Full Name *
              </label>
              <input
                type="text"
                placeholder="e.g. John Doe"
                value={newScanner.name}
                onChange={(e) => setNewScanner({ ...newScanner, name: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                Email *
              </label>
              <input
                type="email"
                placeholder="scanner@example.com"
                value={newScanner.email}
                onChange={(e) => setNewScanner({ ...newScanner, email: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                Password *
              </label>
              <input
                type="password"
                placeholder="Min. 6 characters"
                value={newScanner.password}
                onChange={(e) => setNewScanner({ ...newScanner, password: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                required
                minLength={6}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={isSubmitting}
                className="px-4 py-2 border border-[#dae1e7] rounded-[8px] text-sm font-bold text-[#2b3445] hover:bg-[#f6f9fc] transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-[#1672b9] text-white rounded-[8px] text-sm font-bold hover:bg-[#0f5a9e] transition-all shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] disabled:opacity-50"
              >
                {createScannerMutation.isPending ? "Creating..." : "Create Scanner"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Assign Devices Modal */}
      {showAssignModal && (
        <Modal title="Assign Devices" onClose={() => setShowAssignModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                Select Event
              </label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                disabled={isSubmitting}
              >
                <option value="">Choose Event</option>
                {uniqueEvents.map(event => (
                  <option key={event._id} value={event._id}>{event.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                Select Devices
                {selectedDeviceIds.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-[#1672b9]">
                    {selectedDeviceIds.length} selected
                  </span>
                )}
              </label>
              <div className="max-h-56 overflow-y-auto border border-[#dae1e7] rounded-[8px] p-2 bg-[#f6f9fc]/40">
                {filteredDevices.map(device => {
                  const isOccupied = occupiedIds.has(device._id);
                  const isSelected = selectedDeviceIds.includes(device._id);
                  return (
                    <label
                      key={device._id}
                      className={`flex items-center gap-2 py-2 px-2 rounded-[6px] transition-colors ${
                        isOccupied
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDeviceSelection(device._id)}
                        disabled={isOccupied || isSubmitting}
                        className="w-4 h-4 accent-[#1672b9]"
                      />
                      <span className="text-sm text-[#2b3445] font-medium">
                        {device.name}
                      </span>
                      {isOccupied && (
                        <span className="text-xs text-red-500 ml-auto">
                          Assigned to another scanner
                        </span>
                      )}
                    </label>
                  );
                })}
                {filteredDevices.length === 0 && selectedEventId && (
                  <div className="text-center text-[#708090] py-6 text-sm">
                    No devices found for this event
                  </div>
                )}
                {!selectedEventId && (
                  <div className="text-center text-[#708090] py-6 text-sm">
                    Select an event to see its devices
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowAssignModal(false)}
                disabled={isSubmitting}
                className="px-4 py-2 border border-[#dae1e7] rounded-[8px] text-sm font-bold text-[#2b3445] hover:bg-[#f6f9fc] transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDevices}
                disabled={isSubmitting || selectedDeviceIds.length === 0}
                className="px-4 py-2 bg-[#1672b9] text-white rounded-[8px] text-sm font-bold hover:bg-[#0f5a9e] transition-all shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignDevicesMutation.isPending
                  ? "Assigning..."
                  : `Assign ${selectedDeviceIds.length || ''} Device${selectedDeviceIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// =========================================================================
// Modal Component
// =========================================================================
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[15px] shadow-[0px_4px_16px_0px_rgba(43,52,69,0.1)] max-w-md w-full">
      <div className="flex justify-between items-center p-5 border-b border-[#dae1e7]">
        <h3 className="text-[19.2px] font-semibold text-[#2b3445]">{title}</h3>
        <button
          onClick={onClose}
          className="text-[#708090] hover:text-[#2b3445] transition-colors p-1"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export default AssignScanners;